import { dateComponents } from "@/react/helpers/dateTimeHelpers/dateComponents";
import { formatDateString } from "@/react/helpers/dateTimeHelpers/getLocalizedDate";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import type { MostAttendedEvent } from "@circle-react/apps/EoyRecapApp/types";

interface MostWatchedOrAttendedEventProps {
  event: MostAttendedEvent;
}

export const MostWatchedOrAttendedEvent = ({
  event,
}: MostWatchedOrAttendedEventProps) => {
  const { dayOfMonth, shortMonthString } = dateComponents(event.datetime);
  const monthDay = formatDateString(event.datetime, {
    month: "short",
    day: "numeric",
  });
  const timeRange = formatDateString(event.datetime, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dateTimeDisplay = `${monthDay} ${String.fromCharCode(8226)} ${timeRange}`;
  const remainingAttendees = Math.max(0, event.total_rsvps - 3);

  return (
    <div className="relative w-full max-w-md">
      <div className="relative bg-[#49335A] rounded-2xl p-4 md:p-6 shadow-2xl z-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="bg-[rgba(196,181,204,0.3)] flex p-1 px-3 shrink-0 flex-col items-center justify-center rounded-lg">
            <div className="text-white text-center text-base font-bold">
              {dayOfMonth}
            </div>
            <div className="text-white text-center text-xs font-medium uppercase">
              {shortMonthString}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-white text-base font-semibold leading-tight text-balance line-clamp-3 mb-2">
              {event.name}
            </h3>

            <div className="flex items-center gap-2 text-white">
              <Icon
                type="24-calendar-number"
                className="!size-4 text-gray-300"
              />
              <p className="text-sm text-gray-300">{dateTimeDisplay}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="flex -space-x-2">
              {[0, 1, 2]
                .slice(0, Math.min(3, event.total_rsvps))
                .map(position => (
                  <div
                    key={`avatar-${event.name}-pos-${position}`}
                    className="bg-gray-200 size-8 rounded-full border-2 border-[#49335A]"
                  />
                ))}
            </div>
            {remainingAttendees > 0 && (
              <span className="text-white text-sm font-medium">
                +{remainingAttendees}
              </span>
            )}
          </div>

          <button
            type="button"
            className="bg-[rgba(196,181,204,0.3)] hover:bg-[rgba(196,181,204,0.4)] text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
          >
            {t("events.rsvp.rsvp")}
          </button>
        </div>
      </div>
    </div>
  );
};
